import { cn } from '@/components/ui/utils';
import { Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import React from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useToggle } from 'usehooks-ts';
import { TappIcon } from '../TappIcon';
import {
  TappMenu,
  TappMenuContent,
  TappMenuItem,
  TappMenuSub,
  TappMenuSubContent,
  TappMenuSubtrigger,
  TappMenuTrigger,
} from '../TappMenu';
import { useTappSidebar } from './sidebar.hook';
import { useIsMobile } from '../hooks/device.hook';

export interface TappSidebarSubitem {
  name: TappSidebarItem['name'];
  link?: TappSidebarItem['link'];
  onClick?: TappSidebarItem['onClick'];
  subitems?: Omit<TappSidebarSubitem, 'subitems'>[];
}

export interface TappSidebarItem {
  name: string;
  link?: string;
  icon?: string;
  onClick?: () => any;
  subitems?: TappSidebarSubitem[];
  position?: 'top' | 'bottom';
}

export type TappSidebarRowProps = { item: TappSidebarItem } & Omit<NavLinkProps, 'to'> &
  React.RefAttributes<HTMLAnchorElement>;

export const TappSidebarRow: React.FC<TappSidebarRowProps> = ({ className, item, ...props }) => {
  const isMobile = useIsMobile();
  const sidebar = useTappSidebar();
  const [isSubmenuExpanded, toggleIsSubmenuExpanded, setIsSubmenuExpanded] = useToggle(false);

  const itemClick = (item: TappSidebarItem | TappSidebarSubitem) => {
    if (!item) return;
    if (item.onClick) {
      item.onClick();
    }
    if ('subitems' in item && item.subitems) {
      toggleIsSubmenuExpanded();
    } else if (isMobile) {
      sidebar.close();
    }
  };

  if (!item) return <></>;

  return (
    <div className="contents">
      {item.subitems && sidebar.isCollapsed && (
        <TappMenu open={isSubmenuExpanded} onOpenChange={setIsSubmenuExpanded}>
          <TappMenuTrigger asChild>
            <span className="h-0 w-full"></span>
          </TappMenuTrigger>
          <TappMenuContent className="min-w-[150px]" side="right" align="start" sideOffset={-4}>
            {item.subitems.map((subitem, i) =>
              subitem.subitems ? (
                <TappMenuSub key={i}>
                  <TappMenuSubtrigger>{subitem.name}</TappMenuSubtrigger>
                  <TappMenuSubContent className="min-w-[150px]">
                    {subitem.subitems.map((subSubitem, subi) => (
                      <NavLink key={subi} to={subSubitem.link ?? '#'} end onClick={() => itemClick(subSubitem)}>
                        <TappMenuItem>{subSubitem.name}</TappMenuItem>
                      </NavLink>
                    ))}
                  </TappMenuSubContent>
                </TappMenuSub>
              ) : (
                <NavLink key={i} to={subitem.link ?? '#'} end onClick={() => itemClick(subitem)}>
                  <TappMenuItem>{subitem.name}</TappMenuItem>
                </NavLink>
              )
            )}
          </TappMenuContent>
        </TappMenu>
      )}

      <NavLink
        {...props}
        to={item.link ?? '#'}
        end
        className={({ isActive }) =>
          cn(
            'group flex h-11 w-full cursor-pointer flex-row items-center py-1 transition-all',
            'justify-between pl-6 pr-4',
            {
              'tapp-link-active': isActive && item.link,
              'pl-6 pr-4': !sidebar.isCollapsed,
              'pl-[30px] pr-0': sidebar.isCollapsed,
            },
            className
          )
        }
        onClick={() => itemClick(item)}
        {...props}
      >
        {item.icon && (
          <TappIcon className="text-secondary-foreground transition-all group-[.tapp-link-active]:text-primary group-[:not(.tapp-link-active):hover]:text-foreground/90">
            {item.icon}
          </TappIcon>
        )}

        <Transition
          show={!sidebar.isCollapsed}
          as={React.Fragment}
          enter="transition-all duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-1"
          leave="transition-all duration-500"
          leaveTo="opacity-0"
          leaveFrom="opacity-1"
        >
          <span className="ml-4 grow whitespace-nowrap text-sm font-normal text-secondary-foreground transition group-[.tapp-link-active]:font-semibold group-[.tapp-link-active]:text-foreground group-[:not(.tapp-link-active):hover]:text-foreground/90">
            {item.name}
          </span>
        </Transition>
        {/* )} */}

        {item.subitems && (
          <TappIcon
            className={cn(
              'text-secondary-foreground transition-all duration-300 ease-out group-[.tapp-link-active]:text-primary group-[:not(.tapp-link-active):hover]:text-foreground/90',
              {
                'mr-2 -rotate-90': sidebar.isCollapsed,
                '-rotate-180': !sidebar.isCollapsed && isSubmenuExpanded,
                'rotate-0': !sidebar.isCollapsed && !isSubmenuExpanded,
              }
            )}
            size={sidebar.isCollapsed ? 16 : 20}
          >
            expand_more
          </TappIcon>
        )}
      </NavLink>

      <SidebarRowSubmenu className="ml-9" linkClassName="pl-6" item={item} isSubmenuExpanded={isSubmenuExpanded} />
    </div>
  );
};

const SidebarRowSubmenu = ({
  className = '',
  linkClassName = '',
  item,
  isSubmenuExpanded,
}: {
  className?: string;
  linkClassName?: string;
  item: TappSidebarItem | TappSidebarSubitem;
  isSubmenuExpanded: boolean;
}) => {
  const isMobile = useIsMobile();
  const sidebar = useTappSidebar();
  const [isSubSubmenuExpanded, toggleIsSubSubmenuExpanded] = useToggle(false);

  const itemClick = (item: TappSidebarItem | TappSidebarSubitem) => {
    if (!item) return;
    if (item.onClick) {
      item.onClick();
    }
    if ('subitems' in item && item.subitems) {
      toggleIsSubSubmenuExpanded();
    } else if (isMobile) {
      sidebar.close();
    }
  };

  if (!item.subitems || sidebar.isCollapsed) return <></>;

  return (
    <motion.div
      className="overflow-hidden"
      initial={false}
      animate={{ height: isSubmenuExpanded ? 'auto' : 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        className={cn(
          'relative flex flex-col gap-1 before:absolute before:my-2 before:h-[calc(100%_-_16px)] before:w-px before:bg-border',
          className
        )}
      >
        {item.subitems.map((subitem, index) => (
          <div key={index}>
            <NavLink
              to={subitem.link ?? '#'}
              end
              className={({ isActive }) =>
                cn(
                  'group flex h-8 w-full cursor-pointer flex-row items-center justify-between py-1 pr-4',
                  {
                    'tapp-submenu-active': isActive && subitem.link,
                  },
                  linkClassName
                )
              }
              onClick={() => itemClick(subitem)}
            >
              <span className="grow text-sm font-normal text-secondary-foreground transition group-[.tapp-submenu-active]:font-semibold group-[.tapp-submenu-active]:text-foreground group-[:not(.tapp-link-active):hover]:text-foreground/90">
                {subitem.name}
              </span>

              {'subitems' in subitem && (
                <TappIcon
                  className={cn(
                    'mr-4 text-secondary-foreground transition-all duration-300 ease-out group-[.tapp-link-active]:text-primary group-[:not(.tapp-link-active):hover]:text-foreground/90',
                    {
                      '-rotate-180': isSubSubmenuExpanded,
                      'rotate-0': !isSubSubmenuExpanded,
                    }
                  )}
                >
                  expand_more
                </TappIcon>
              )}
            </NavLink>

            {'subitems' in subitem && (
              <SidebarRowSubmenu
                className="ml-7"
                linkClassName="pl-3"
                item={subitem}
                isSubmenuExpanded={isSubSubmenuExpanded}
              />
            )}
          </div>
        ))}
      </div>
    </motion.div>
  );
};
